import { useMemo } from "react";
import { Auth } from "@aws-amplify/auth";
import { Form, Input, message, Row, Col, Typography, Divider } from "antd";
import { Button, LanguageChange } from "shared/components";
import { Trans, useTranslation } from "react-i18next";
import { RouteComponentProps } from "react-router";
import { ReactComponent as Logo } from "shared/assets/images/logo-strm.svg";
import { ReactComponent as MailIcon } from "shared/assets/images/icon-mail.svg";
import { ReactComponent as LockIcon } from "shared/assets/images/icon-lock.svg";
import { ReactComponent as UserIcon } from "shared/assets/images/icon-meus-dados.svg";
import { registerUser } from "shared/service/StrmService";
import { Link } from "react-router-dom";
import { isMobile } from "react-device-detect";
import RecordGirl from "shared/assets/images/record-girl-tall.png";
import { ReactComponent as IconCheck } from "shared/assets/images/icon-check.svg";
import "./CadastreSe.scss";

const CadastreSe: React.FC<RouteComponentProps> = (props) => {
  const { history } = props;
  const [form] = Form.useForm();
  const { t, i18n } = useTranslation();

  const password = Form.useWatch("password", form) || "";

  const passwordRules = useMemo(() => {
    return [
      {
        label: t("pages.CadastreSe.umaLetraMaiuscula"),
        regex: /[A-Z]/,
      },
      {
        label: t("pages.CadastreSe.umCaractereEspecial"),
        regex: /[^A-Za-z0-9]/,
      },
      {
        label: t("pages.CadastreSe.umNumero"),
        regex: /[0-9]/,
      },
      {
        label: t("pages.CadastreSe.oitoCaracteres"),
        regex: /.{8,}/,
      },
    ];
  }, [t]);

  const onFinish = async () => {
    const values = await form.validateFields();
    try {
      const user = await Auth.signUp({
        username: values.email,
        password: values.password,
        attributes: {
          name: values.name,
          locale: i18n.language,
        },
      });

      await registerUser({
        cognitoUsername: user.userSub,
        name: values.name,
        login: values.email,
        currentLanguage: i18n.language,
      });

      message.success(t("pages.CadastreSe.mensagemSucesso"), 10);
      history.push("/verificacao", {
        login: values.email,
      });
    } catch (error: any) {
      if (error.code === "UsernameExistsException")
        message.error(t("pages.CadastreSe.usuarioExistente"), 10);
      else if (error.code === "InvalidPasswordException")
        message.error(t("pages.CadastreSe.senhaEmNaoConformidade"), 10);
      else message.error(error.message, 10);
    }
  };

  return (
    <Row className="login cadastre-se" style={{ paddingTop: 32 }}>
      {isMobile && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            width: "100%",
          }}
        >
          <LanguageChange.SelectLanguageChange />
        </div>
      )}
      <Col
        xs={isMobile ? 24 : 10}
        style={{ display: "flex", justifyContent: "flex-end" }}
      >
        <div className="container" style={{ padding: isMobile ? 12 : 0 }}>
          <Row className="fedarated-buttons" gutter={18}>
            <Col xs={24}>
              <div className="logo-region">
                <Logo className="logo" style={{ width: isMobile ? 80 : 100 }} />
                <span style={{ fontSize: isMobile ? 14 : 28 }}>for labels</span>
              </div>
            </Col>
            <Col span={24}>
              <Typography.Title className="text login-title">
                {t("pages.CadastreSe.titulo")}
              </Typography.Title>
              <Typography.Text className="text login-subtitle">
                {t("pages.Login.texto")}
              </Typography.Text>
              <Typography.Text className="text social">
                {t("pages.CadastreSe.socialText")}
              </Typography.Text>
            </Col>
            <Col xs={12}>
              <Button.Google />
            </Col>
            <Col span={12}>
              <Button.Facebook />
            </Col>
          </Row>
          <Divider className="divider" style={{ margin: "12px 0" }}>
            {t("pages.Login.ou")}
          </Divider>
          <Row>
            <Col span={24}>
              <Form
                layout={"vertical"}
                form={form}
                onFinish={onFinish}
                autoComplete="off"
              >
                <div className="content" style={{ marginBottom: 12 }}>
                  <div className="input">
                    <Form.Item
                      name="name"
                      rules={[
                        {
                          required: true,
                          message: t("pages.CadastreSe.nameRequired"),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t("pages.CadastreSe.nome")}
                        prefix={<UserIcon className="icon-color" />}
                        size="large"
                      />
                    </Form.Item>
                    <Form.Item
                      name="email"
                      rules={[
                        {
                          required: true,
                          message: t("pages.CadastreSe.emailRequired"),
                        },
                      ]}
                    >
                      <Input
                        placeholder={t("pages.CadastreSe.email")}
                        prefix={<MailIcon />}
                        size="large"
                      />
                    </Form.Item>

                    <Form.Item
                      name="password"
                      rules={[
                        {
                          required: true,
                          message: t("pages.CadastreSe.passwordRequired"),
                        },
                      ]}
                      extra={t("pages.CadastreSe.criterioSenha")}
                    >
                      <Input.Password
                        placeholder={t("pages.CadastreSe.senha")}
                        size="large"
                        prefix={<LockIcon />}
                      />
                    </Form.Item>

                    <div className="password-rules">
                      {passwordRules.map((rule, index) => {
                        const isValid = rule.regex.test(password);
                        return (
                          <div
                            key={index}
                            className={`rule ${isValid ? "valid" : ""}`}
                          >
                            <IconCheck className="check-icon" />
                            <span>{rule.label}</span>
                          </div>
                        );
                      })}
                    </div>

                    <Form.Item
                      name="confirm"
                      dependencies={["password"]}
                      rules={[
                        {
                          required: true,
                          message: t("pages.CadastreSe.confirmRequired"),
                        },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue("password") === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(
                              new Error(t("pages.CadastreSe.senhaNaoCombina"))
                            );
                          },
                        }),
                      ]}
                    >
                      <Input.Password
                        placeholder={t("pages.CadastreSe.confirmaSenha")}
                        prefix={<LockIcon />}
                        size="large"
                      />
                    </Form.Item>
                  </div>

                  <div className="enter-button">
                    <button
                      className="botao-roxo"
                      disabled={passwordRules.some(
                        (rule) => !rule.regex.test(password)
                      )}
                    >
                      {t("pages.CadastreSe.cadastrar")}
                    </button>
                  </div>
                </div>
              </Form>
            </Col>
          </Row>
          <Row className="row-register">
            <Col span={24}>
              <div className="text-register back-login">
                {t("pages.CadastreSe.possuiCadastro")}
                <Link style={{ marginLeft: 6 }} to="/login">
                  Login
                </Link>
              </div>
            </Col>
          </Row>
          <span
            style={{
              display: "flex",
              borderBottom: "1px solid #272727",
              marginTop: 22,
            }}
          />

          <span className="outras-plataformas-titulo">
            {t("pages.Login.acesseOutraPlataforma")}
          </span>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <a
              href={process.env.REACT_APP_STRM_ARTIST_URL}
              className="outras-plataformas"
            >
              <span>
                <Logo style={{ width: 67 }} />
                for labels
              </span>
            </a>
          </div>
        </div>
      </Col>
      <Col xs={14} hidden={isMobile}>
        <div className="caixa-copy">
          <div className="copy-content">
            {!isMobile && <LanguageChange.SelectLanguageChange />}
            <img src={RecordGirl} alt="girl playing drum" />
            <div className="copy">
              <h1>{t("pages.Login.exploreSuaCarreira")}</h1>
              <ul>
                <li className="azul">
                  <Trans i18nKey={t("pages.Login.suaCarreiraUm")} />
                </li>
                <li className="roxo">
                  <Trans i18nKey={t("pages.Login.suaCarreiraDois")} />
                </li>
                <li className="rosa">
                  <Trans i18nKey={t("pages.Login.suaCarreirTres")} />
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Col>
    </Row>
  );
};
export default CadastreSe;
